// src/pages/AnalyticsPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useProgram } from '../context/ProgramContext';
import apiClient from '../api';
import '../styles/AnalyticsPage.css';

const formatPointsIssued = (baseUnits, program) => {
    if (program?.asset_type === 'points_on_chain') {
        const dec = program.asa_decimals || 0;
        const factor = Math.pow(10, dec);
        const value = Number(baseUnits || 0) / factor;
        const unit = program.asa_unit_name || 'tokens';
        return `${value.toLocaleString(undefined, { maximumFractionDigits: dec })} ${unit}`;
    }
    return `${Number(baseUnits || 0).toLocaleString()} pts`;
};

const BarChart = ({ buckets, label }) => {
    if (!buckets || buckets.length === 0) {
        return <p className="info-message">No data yet.</p>;
    }
    const max = Math.max(1, ...buckets.map(b => b.count));
    return (
        <div className="bar-chart">
            <div className="bars">
                {buckets.map((b, i) => {
                    const heightPct = (b.count / max) * 100;
                    return (
                        <div key={i} className="bar-col" title={`${b.period}: ${b.count}`}>
                            <div className="bar" style={{ height: `${heightPct}%` }}>
                                {b.count > 0 && <span className="bar-value">{b.count}</span>}
                            </div>
                            <div className="bar-label">{b.period.replace(/^\d{4}-/, '')}</div>
                        </div>
                    );
                })}
            </div>
            <p className="chart-caption">{label}</p>
        </div>
    );
};

const MethodBar = ({ breakdown }) => {
    const entries = Object.entries(breakdown || {});
    const total = entries.reduce((s, [, v]) => s + (v || 0), 0);
    if (total === 0) return <p className="info-message">No signups yet.</p>;
    const colors = { email: '#3498db', google: '#e67e22', pera: '#27ae60' };
    return (
        <div className="method-breakdown">
            <div className="method-bar">
                {entries.map(([k, v]) => v > 0 && (
                    <div
                        key={k}
                        className="method-segment"
                        style={{ width: `${(v / total) * 100}%`, backgroundColor: colors[k] || '#888' }}
                        title={`${k}: ${v}`}
                    />
                ))}
            </div>
            <ul className="method-legend">
                {entries.map(([k, v]) => (
                    <li key={k}>
                        <span className="legend-swatch" style={{ backgroundColor: colors[k] || '#888' }} />
                        <strong>{k}</strong>: {v} ({total ? Math.round((v / total) * 100) : 0}%)
                    </li>
                ))}
            </ul>
        </div>
    );
};

const StatCard = ({ label, value, sub }) => (
    <div className="stat-card">
        <div className="stat-label">{label}</div>
        <div className="stat-value">{value}</div>
        {sub && <div className="stat-sub">{sub}</div>}
    </div>
);

const AnalyticsPage = ({ isAuthenticated }) => {
    const { selectedProgram } = useProgram();
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [weeks, setWeeks] = useState(8);

    const fetch = useCallback(async () => {
        if (!selectedProgram) { setData(null); return; }
        setIsLoading(true); setError('');
        try {
            const res = await apiClient.get(`/programs/${selectedProgram.id}/analytics`, { params: { weeks } });
            setData(res.data);
        } catch (err) {
            setError(err.response?.data?.detail || err.message || 'Failed to load analytics.');
            setData(null);
        } finally {
            setIsLoading(false);
        }
    }, [selectedProgram, weeks]);

    useEffect(() => {
        if (isAuthenticated && selectedProgram) fetch();
        else { setData(null); setIsLoading(false); setError(''); }
    }, [isAuthenticated, selectedProgram, fetch]);

    if (!isAuthenticated) return <Navigate to="/dashboard" replace />;

    if (!selectedProgram) {
        return (
            <div className="analytics-container info-message">
                <h2>No Program Selected</h2>
                <p>Please <Link to="/select-program">select a program</Link> first to view analytics.</p>
            </div>
        );
    }

    return (
        <div className="analytics-container">
            <div className="analytics-header">
                <div>
                    <h2>Analytics</h2>
                    <p>Live metrics for <strong>{selectedProgram.name}</strong> ({selectedProgram.asset_type === 'points_on_chain' ? 'on-chain — read live from the indexer' : 'off-chain points'}).</p>
                </div>
                <div className="weeks-control">
                    <label>
                        Weeks
                        <select value={weeks} onChange={(e) => setWeeks(parseInt(e.target.value, 10))} disabled={isLoading}>
                            <option value={4}>Last 4</option>
                            <option value={8}>Last 8</option>
                            <option value={12}>Last 12</option>
                            <option value={26}>Last 26</option>
                        </select>
                    </label>
                    <button onClick={fetch} disabled={isLoading}>{isLoading ? 'Loading…' : 'Refresh'}</button>
                </div>
            </div>

            {error && <p className="error-message">Error: {error}</p>}
            {isLoading && !data && <p className="loading-message">Loading analytics…</p>}

            {data && (
                <>
                    <div className="stat-grid">
                        <StatCard label="Participants" value={data.participant_count.toLocaleString()} />
                        <StatCard
                            label={data.asset_type === 'points_on_chain' ? 'Tokens issued (live)' : 'Points issued'}
                            value={formatPointsIssued(data.points_issued_total, data)}
                            sub={data.asset_type === 'points_on_chain' ? 'Sum of participant balances on-chain' : 'Current balances + redeemed'}
                        />
                        <StatCard label="Pending redemptions" value={data.redemption_count_pending} />
                        <StatCard label="Fulfilled redemptions" value={data.redemption_count_fulfilled} />
                        <StatCard label="Cancelled redemptions" value={data.redemption_count_cancelled} />
                    </div>

                    <section className="analytics-section">
                        <h3>Signups per week</h3>
                        <BarChart buckets={data.signups_by_week} label={`ISO weeks (${data.signups_by_week.length})`} />
                    </section>

                    <div className="analytics-row">
                        <section className="analytics-section">
                            <h3>Signup methods</h3>
                            <MethodBar breakdown={data.signup_method_breakdown} />
                        </section>

                        <section className="analytics-section">
                            <h3>Top rewards</h3>
                            {data.top_rewards.length === 0 ? (
                                <p className="info-message">No redemptions recorded yet.</p>
                            ) : (
                                <table className="top-rewards-table">
                                    <thead><tr><th>Reward</th><th>Redemptions</th></tr></thead>
                                    <tbody>
                                        {data.top_rewards.map(r => (
                                            <tr key={r.reward_id}>
                                                <td>{r.title}</td>
                                                <td>{r.redemption_count}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </section>
                    </div>
                </>
            )}
        </div>
    );
};

export default AnalyticsPage;
